import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { AnalyzerQueueItemView, AnalyzerQueueReason, LabSampleView } from '@wodi/shared';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  Dialog,
  DialogActions,
  EmptyState,
  formatShortAge,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  SegmentedControl,
  Table,
  TableBody,
  TableHeader,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  analyzerKeys,
  analyzerQueueQuery,
  analyzersQuery,
  applyAnalyzerResult,
  dismissAnalyzerResult,
  type AnalyzerQueueState,
} from '../api/lab-analyzers.ts';
import { findLabSample, labKeys } from '../api/lab.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ConnectionTag, lastSeenText } from '../lab/analyzer-state.tsx';
import styles from '../lab/lab.module.css';
import { TableScroll } from '../layout/screen.tsx';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

// Analyzer results to review (docs/plans/phase2-lab-connector.md §4.3, §6). Results an
// analyzer sent that could not be applied by themselves wait here with the reason. A person
// applies one, with a corrected sample number when the barcode was misread, or dismisses it
// with a reason. Applied results are unvalidated: they still go through submit and validate.

const VIEWS = [
  { id: 'queued', label: 'To review' },
  { id: 'applied', label: 'Applied' },
  { id: 'dismissed', label: 'Dismissed' },
] as const;

const EMPTY: Record<AnalyzerQueueState, string> = {
  queued: 'Nothing to review. Results that match a sample and a request are applied by themselves.',
  applied: 'No analyzer results applied yet.',
  dismissed: 'No analyzer results dismissed.',
};

/** Reasons a corrected sample number can fix. */
const SAMPLE_REASONS: readonly AnalyzerQueueReason[] = [
  'sample_not_found',
  'sample_not_usable',
  'test_not_ordered',
];

export function LabAnalyzerResultsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const [state, setState] = useState<AnalyzerQueueState>('queued');
  const [applying, setApplying] = useState<AnalyzerQueueItemView | null>(null);
  const [dismissing, setDismissing] = useState<AnalyzerQueueItemView | null>(null);
  const online = useOnline();
  const list = useQuery({ ...analyzerQueueQuery(state), refetchInterval: 30_000 });
  if (session === null || !can(session, 'lab.read')) {
    return (
      <>
        <PageHeader title="Analyzer results" />
        <Notice tone="info">
          You don&apos;t have permission to see lab work. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  const mayReview = can(session, 'lab.analyzers.review') && can(session, 'lab.result.enter');
  return (
    <div className={styles.page}>
      <PageHeader title="Analyzer results" actions={<Link to="/lab">Back to the lab</Link>} />
      <AnalyzerStates />
      <Section
        title={VIEWS.find((v) => v.id === state)?.label ?? ''}
        actions={
          <SegmentedControl
            aria-label="Which results"
            options={VIEWS}
            value={state}
            onChange={(id) => {
              setState(id as AnalyzerQueueState);
            }}
          />
        }
      >
        {list.isPending ? <LoadingBlock label="Loading analyzer results" rows={3} /> : null}
        {list.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void list.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(list.error, "Analyzer results couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {!online && list.isSuccess ? (
          <Notice tone="info">Offline. Applying or dismissing needs a connection.</Notice>
        ) : null}
        {list.isSuccess && list.data.items.length === 0 ? (
          <EmptyState>{EMPTY[state]}</EmptyState>
        ) : null}
        {list.isSuccess && list.data.items.length > 0 ? (
          <TableScroll>
            <Table aria-label="Analyzer results" flat>
              <TableHeader>
                <Column isRowHeader>Sample no.</Column>
                <Column>Received</Column>
                <Column>Test</Column>
                <Column numeric>Value</Column>
                <Column>Analyzer flag</Column>
                <Column>{state === 'queued' ? 'Why it waits' : 'Resolved'}</Column>
                <Column>
                  <span className={styles.meta}>Action</span>
                </Column>
              </TableHeader>
              <TableBody items={list.data.items}>
                {(item) => (
                  <Row id={item.id}>
                    <Cell>
                      <span className="wodi-id">{item.sampleNumber}</span>
                    </Cell>
                    <Cell>
                      <span className={styles.stack}>
                        <span>{formatInstant(item.receivedAt)}</span>
                        <span className={styles.meta}>{item.analyzerName}</span>
                      </span>
                    </Cell>
                    <Cell>
                      <span className={styles.stack}>
                        <span>{item.analyteName ?? 'Not mapped'}</span>
                        <span className={`${styles.meta} wodi-id`}>{item.analyzerTestCode}</span>
                      </span>
                    </Cell>
                    <Cell
                      numeric
                    >{`${item.value}${item.unit === null ? '' : ` ${item.unit}`}`}</Cell>
                    <Cell>{item.analyzerFlags ?? 'None'}</Cell>
                    <Cell>
                      {state === 'queued'
                        ? (item.reasonText ?? '')
                        : item.resolution === null
                          ? 'Applied automatically'
                          : `${item.resolution.by.displayName}${item.resolution.note === null ? '' : `: ${item.resolution.note}`}`}
                    </Cell>
                    <Cell>
                      {state === 'queued' && mayReview ? (
                        <span className={styles.actions}>
                          <Button
                            dense
                            isDisabled={!online}
                            aria-label={`Apply result for sample ${item.sampleNumber}`}
                            onPress={() => {
                              setApplying(item);
                            }}
                          >
                            Apply
                          </Button>
                          <Button
                            dense
                            variant="quiet"
                            isDisabled={!online}
                            aria-label={`Dismiss result for sample ${item.sampleNumber}`}
                            onPress={() => {
                              setDismissing(item);
                            }}
                          >
                            Dismiss
                          </Button>
                        </span>
                      ) : item.sampleId !== null && state === 'applied' ? (
                        <Link to="/lab/samples/$sampleId" params={{ sampleId: item.sampleId }}>
                          Open sample
                        </Link>
                      ) : null}
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        ) : null}
        {!mayReview && state === 'queued' && list.isSuccess && list.data.items.length > 0 ? (
          <p className={styles.meta}>
            Applying or dismissing needs the permission to review analyzer results. Ask the lab
            manager.
          </p>
        ) : null}
      </Section>
      {applying === null ? null : (
        <ApplyDialog
          item={applying}
          onClose={() => {
            setApplying(null);
          }}
        />
      )}
      {dismissing === null ? null : (
        <DismissDialog
          item={dismissing}
          onClose={() => {
            setDismissing(null);
          }}
        />
      )}
    </div>
  );
}

/** Each analyzer's connection, so the lab sees at once when one has stopped sending. */
function AnalyzerStates() {
  const analyzers = useQuery({ ...analyzersQuery, refetchInterval: 30_000 });
  if (analyzers.isPending) return <LoadingBlock label="Loading analyzers" rows={1} />;
  if (analyzers.isError) {
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void analyzers.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(analyzers.error, "The analyzers' state couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const active = analyzers.data.filter((a) => a.status === 'active');
  if (active.length === 0) return null;
  return (
    <Section title="Analyzers">
      <ul className={styles.list}>
        {active.map((a) => (
          <li key={a.id} className={styles.listRow}>
            <span className={styles.stack}>
              <strong>{a.name}</strong>
              <span className={styles.meta}>{lastSeenText(a)}</span>
            </span>
            <ConnectionTag analyzer={a} />
            <span className={styles.meta}>
              {a.lastError === null ? '' : `Last problem: ${a.lastError.text}`}
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function ApplyDialog({ item, onClose }: { item: AnalyzerQueueItemView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [sampleNumber, setSampleNumber] = useState('');
  const [replaceEntered, setReplaceEntered] = useState(false);
  const [acceptNotFinal, setAcceptNotFinal] = useState(false);
  const corrected = sampleNumber.trim();
  // A typed sample number is never applied blind: the sample it names, with its patient and
  // tests, is shown first, so a slip of the keyboard can't file a result on someone else.
  const find = useMutation({ mutationFn: () => findLabSample(corrected) });
  const found: LabSampleView | null = find.data?.sampleNumber === corrected ? find.data : null;
  const needsFind = corrected !== '' && found === null;
  const apply = useMutation({
    mutationFn: () =>
      applyAnalyzerResult(item.id, {
        ...(corrected === '' ? {} : { sampleNumber: corrected }),
        replaceEntered,
        acceptNotFinal,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: analyzerKeys.all });
      await queryClient.invalidateQueries({ queryKey: labKeys.all });
      toasts.show({
        title: 'Result applied',
        description: 'It is unvalidated until submitted and validated.',
      });
      onClose();
    },
  });
  return (
    <Dialog
      title="Apply analyzer result"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p>
          <span>{`${item.analyteName ?? item.analyzerTestCode} ${item.value}${item.unit === null ? '' : ` ${item.unit}`} for sample `}</span>
          <span className="wodi-id">{item.sampleNumber}</span>
          <span>{`, from ${item.analyzerName}. ${item.reasonText ?? ''}`}</span>
        </p>
        {item.reason !== null && SAMPLE_REASONS.includes(item.reason) ? (
          <TextField
            label="Correct sample number"
            description="Only if the analyzer misread the barcode. Check it against the tube."
            width="md"
            value={sampleNumber}
            onChange={(value) => {
              setSampleNumber(value);
              find.reset();
            }}
            autoFocus
          />
        ) : null}
        {found === null ? null : <FoundSample sample={found} />}
        {find.isError ? (
          <Notice tone="error">
            {errorText(find.error, "The sample couldn't be found. Check the number and try again.")}
          </Notice>
        ) : null}
        {item.reason === 'differs_from_entered' ? (
          <Checkbox isSelected={replaceEntered} onChange={setReplaceEntered}>
            Replace the value already entered (it is kept in the history)
          </Checkbox>
        ) : null}
        {item.reason === 'status_not_final' ? (
          <Checkbox isSelected={acceptNotFinal} onChange={setAcceptNotFinal}>
            Apply although the analyzer marked it not final
          </Checkbox>
        ) : null}
        <p className={styles.meta}>
          You become the person who entered it. It still needs submitting and validating.
        </p>
        {apply.isError ? (
          <Notice tone="error">
            {errorText(apply.error, "The result wasn't applied. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        {needsFind ? (
          <Button
            variant="primary"
            isPending={find.isPending}
            pendingLabel="Finding"
            onPress={() => {
              find.mutate();
            }}
          >
            Find sample
          </Button>
        ) : (
          <Button
            variant="primary"
            isPending={apply.isPending}
            pendingLabel="Applying"
            onPress={() => {
              apply.mutate();
            }}
          >
            {found === null ? 'Apply result' : 'Apply to this sample'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** The sample a corrected number names: who it belongs to and what was ordered on it. */
function FoundSample({ sample }: { sample: LabSampleView }) {
  return (
    <dl className={styles.stack} aria-label="Sample found">
      <div>
        <dt className={styles.meta}>Sample</dt>
        <dd className="wodi-id">{sample.sampleNumber}</dd>
      </div>
      <div>
        <dt className={styles.meta}>Patient</dt>
        <dd>
          <strong>{sample.patient.displayName}</strong>
          <span>{`, ${sample.patient.sex.display}, ${formatShortAge(sample.patient.age, false)} · `}</span>
          <span className="wodi-id">{sample.patient.patientNumber}</span>
        </dd>
      </div>
      <div>
        <dt className={styles.meta}>Tests</dt>
        <dd>{sample.orders.map((o) => o.testName).join(', ')}</dd>
      </div>
    </dl>
  );
}

function DismissDialog({ item, onClose }: { item: AnalyzerQueueItemView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const short = reason.trim().length < 5;
  const dismiss = useMutation({
    mutationFn: () => dismissAnalyzerResult(item.id, reason.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: analyzerKeys.all });
      toasts.show({ title: 'Result dismissed' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Dismiss analyzer result"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p>
          <span>Sample </span>
          <span className="wodi-id">{item.sampleNumber}</span>
          <span>{`, ${item.analyteName ?? item.analyzerTestCode} ${item.value}. It stays in the list of dismissed results.`}</span>
        </p>
        <TextArea
          label="Reason"
          description="For example: quality control material, entered by hand, sample rerun."
          value={reason}
          onChange={setReason}
          {...(tried && short ? { errorMessage: 'Say why, in a few words.' } : {})}
        />
        {dismiss.isError ? (
          <Notice tone="error">
            {errorText(dismiss.error, "The result wasn't dismissed. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={dismiss.isPending}
          pendingLabel="Dismissing"
          onPress={() => {
            setTried(true);
            if (!short) dismiss.mutate();
          }}
        >
          Dismiss result
        </Button>
      </DialogActions>
    </Dialog>
  );
}
